import React from 'react';
import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';
import type { AddOnSelectorProps, AddOnScheduleData } from '@/features/booking/types/ticketBookingTypes';

interface MobileAddOnSelectorProps extends Omit<AddOnSelectorProps, 'clubHandle'> {
  currency?: string;
}

const MobileAddOnSelector: React.FC<MobileAddOnSelectorProps> = ({
  addOns,
  selectedAddOns,
  onAddOnToggle,
  onAddOnClick,
  guestCount,
  currency,
}) => {
  // Format price using currency if provided, otherwise show raw amount
  const formatPrice = (amount: number) => currency 
    ? formatCurrencyForDisplay(amount, currency) 
    : `${(amount / 100).toFixed(2)}`;

  const handleCardClick = (addOnSchedule: AddOnScheduleData) => {
    onAddOnClick(addOnSchedule);
  };

  const isAddOnSelected = (addOnId: string) => {
    return selectedAddOns[addOnId] > 0;
  };

  if (addOns.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4 px-4 pb-8">
      <div className="flex flex-col mb-4">
        <h2 className="text-xl font-bold text-white tracking-tight">
          Add-ons
        </h2>
      </div>
      
      {/* Add-ons Grid - 2 columns on mobile */}
      <div className="grid grid-cols-2 gap-3 w-full">
        {addOns.map((addOnSchedule) => {
          const addOn = addOnSchedule.add_on;
          const isSelected = isAddOnSelected(addOn.id);
          const isUnlimited = addOnSchedule.remaining_inventory === null;
          const isInventoryLimited = !isUnlimited && (addOnSchedule.remaining_inventory as number) < guestCount;
          
          const hasValidImage = addOn.image_id && addOn.image_id !== '00000000-0000-0000-0000-000000000000';
          const imageUrl = hasValidImage ? getSignedMediaUrl(addOn.image_id, 'image').url : null;

          // If guestCount is 0, the table isn't added yet, so we disable the toggle
          const isOutOfStock = !isUnlimited && addOnSchedule.remaining_inventory === 0;
          const isDisabled = guestCount === 0 || isOutOfStock;

          return (
            <div 
              key={addOn.id}
              className={`relative aspect-[3/2] rounded-2xl overflow-hidden group cursor-pointer transition-opacity ${isDisabled ? 'opacity-50' : 'opacity-100'}`}
              onClick={() => {
                if (!isDisabled) {
                  handleCardClick(addOnSchedule);
                }
              }}
            >
              {/* Background Image */}
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: imageUrl 
                    ? `url(${imageUrl})` 
                    : 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
                }}
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/40" />
              
              {/* Content */}
              <div className="relative h-full p-3 flex flex-col justify-between">
                {/* Checkbox / Toggle Style */}
                <div className="flex justify-end">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isDisabled) {
                        onAddOnToggle(addOn.id, !isSelected);
                      }
                    }}
                    disabled={isDisabled}
                    className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                      isDisabled
                        ? 'bg-gray-600 cursor-not-allowed opacity-50'
                        : isSelected 
                        ? 'bg-blue-600' 
                        : 'bg-white/30'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                        isSelected ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                {/* Add-on title and price */}
                <div className="space-y-1">
                  <h3 className="text-sm font-bold text-white line-clamp-2 leading-tight">
                    {addOn.name}
                  </h3>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-bold text-blue-400">
                      {formatPrice(addOnSchedule.price)}
                    </div>
                  </div>
                  
                  {isInventoryLimited && !isDisabled && (
                    <div className="text-[9px] font-bold text-red-400 uppercase tracking-wider">
                      Limited
                    </div>
                  )}
                  {isDisabled && (
                    <div className="text-[9px] font-bold text-blue-300 uppercase tracking-wider">
                      Add table first
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MobileAddOnSelector;
